import { AfterViewInit, Component, Inject, Injector, OnDestroy, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Shift } from 'src/app/models/shift.model';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { ShiftService } from 'src/app/services/shift.service';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { ShiftDialogComponent } from 'src/app/modals/shift-dialog/shift-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { ShiftType } from 'src/app/enums/shiftTypeValue.enum';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { AuthService } from 'src/app/services';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { Observable, of, Subscription } from 'rxjs';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { Lookup } from 'src/app/models/lookup.model';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { workforceRouteConfig } from 'src/app/shared/constants/workforceRouteConfig.constants';
import { WorkforceTabsService } from 'src/app/services/workforce-tabs.service';
import { GridView } from 'src/app/models/gridView.model';

@Component({
  standalone: false,
    selector: 'app-shift-list',
    templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss'],
})
export class ShiftListComponent extends CoreGridComponent<Shift> implements OnInit, AfterViewInit, OnDestroy {

    organizationLevels: Lookup[] = [];
    private workforceTabsSub: Subscription;

    constructor(
        protected shiftService: ShiftService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private organizationLevelService: OrganizationLevelService,
        private workforceTabsService: WorkforceTabsService,
        private dateFormatService: DateFormatService,
        protected injector: Injector,
        @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
    ) {
        super(toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService
        );
        this.gridService = shiftService;
    }

    protected get objectId(): string {
        return RolePermissionObjects.Shift;
    }

    protected override get isGridViewEnabled(): boolean { return true; }

    protected override get deferGridViewInit(): boolean { return true; }

    override initializeProperties(): void {
        this.addEntityTitle = 'Shift';
        this.searchPlaceholder = 'Search Shifts';
    }

    protected override async onGridViewInitialized(view: GridView | null): Promise<void> {
        // Inject shiftLengthHours before super runs mapColumns so the base sets displayedColumns normally
        if (view?.settingsJson?.columns?.length > 0) {
            const prop = nameOf<Shift>(x => x.shiftLengthHours) as string;
            if (!view.settingsJson.columns.some(c => c.property === prop)) {
                const maxOrder = Math.max(...view.settingsJson.columns.map(c => c.order));
                view.settingsJson.columns.push({ property: prop, visible: true, order: maxOrder + 1 });
            }
        }
        await super.onGridViewInitialized(view);
    }

    override initializeColumns(): void {
        this.additionalActions = [];
        this.columnConfigs = [
            { name: nameOf<Shift>(x => x.name), displayName: 'Name' },
            { name: nameOf<Shift>(x => x.code), displayName: 'Code' },
            {
                name: nameOf<Shift>(x => x.shiftType),
                displayName: 'Shift Type',
                cellTemplate: (row) => row.shiftType != null ? this.getShiftTypeDisplayName(row.shiftType) : '-',
                enumFilterMap: this.getShiftTypeFilterMap()
            },
            { name: nameOf<Shift>(x => x.startTime), displayName: 'Start Time', cellTemplate: (row) => row.startTime ? this.formatTimeValue(row.startTime) : '-' },
            { name: nameOf<Shift>(x => x.endTime), displayName: 'End Time', cellTemplate: (row) => row.endTime ? this.formatTimeValue(row.endTime) : '-' },
            { name: nameOf<Shift>(x => x.shiftLengthHours), displayName: 'Shift Length (hrs)', cellTemplate: (row) => row.shiftLengthHours != null ? row.shiftLengthHours.toFixed(2) : '-' },
            { name: nameOf<Shift>(x => x.maxWeeklyHours), displayName: 'Max Weekly Hours', cellTemplate: (row) => row.maxWeeklyHours != null ? row.maxWeeklyHours.toString() : '-' },
            { name: nameOf<Shift>(x => x.averageWeeklyHours), displayName: 'Avg Weekly Hours', cellTemplate: (row) => row.averageWeeklyHours != null ? row.averageWeeklyHours.toString() : '-' },
            { name: nameOf<Shift>(x => x.effectiveStartDate), displayName: 'Effective Start', cellTemplate: (row) => row.effectiveStartDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(row.effectiveStartDate, this.dateFormatService.userDateFormat) : '-' },
            { name: nameOf<Shift>(x => x.effectiveEndDate), displayName: 'Effective End', cellTemplate: (row) => row.effectiveEndDate ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(row.effectiveEndDate, this.dateFormatService.userDateFormat) : '-' },
            {
                name: nameOf<Shift>(x => x.organizationLevelId),
                displayName: 'Organization Level',
                cellTemplate: (row) => row.organizationLevelName || '-'
            },
            { name: 'action', displayName: 'Actions' }
        ];
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<Shift>(x => x.organizationLevelId): return this.organizationLevelService;
            default: return undefined;
        }
    }

    get hasOrgFilter() {
        return true;
    }

    ngOnInit(): void {
        this.searchDialogRef = this.injector.get(MatDialogRef<ShiftListComponent>, null);
        super.ngOnInit();
        this.initializeWorkforceTabs();
        this.organizationLevelService.getAll().subscribe({
            next: (orgs) => this.organizationLevels = orgs,
            error: () => {
                this.toastService.displayErrorMessage('Error loading organization levels', true);
            }
        });
        this.startGridViewInitialization();
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs() {
        return [...workforceRouteConfig];
    }

    private initializeWorkforceTabs(): void {
        this.workforceTabsSub = this.workforceTabsService.getWorkforceTabs().subscribe(tabs => {
            this.tabs = tabs;
            this.selectedTabIndex = tabs.findIndex(
                link => link.routerLink === '/dashboard/workforce/shifts'
            );
        });
    }

    ngOnDestroy(): void {
        this.workforceTabsSub?.unsubscribe();
        // Chain to the base class so CoreGridComponent/BaseGridComponent can clear
        // drill-down overrides and tear down their own subscriptions.
        super.ngOnDestroy();
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(shiftId?: string): void {
        const shiftModel: IFormDialogData = {
            id: shiftId
        };
        this.openDialog(ShiftDialogComponent, shiftModel);
    }

    override openEntityDeleteDialog(shift: Shift): void {
        this.openDeleteDialog(DeleteDialog, shift, this.shiftService, 'Shift');
    }

    private getShiftTypeDisplayName(value: number): string {
        const name = ShiftType[value];
        if (!name) return '-';
        return name.replace('Shift_', '');
    }

    private getShiftTypeFilterMap(): { value: number; name: string }[] {
        return Object.keys(ShiftType)
            .filter(key => !isNaN(Number(key)))
            .map(key => ({
                value: Number(key),
                name: ShiftType[Number(key)].replace('Shift_', '')
            }));
    }

    private formatTimeValue(time: string): string {
        const parsed = this.dateFormatService.parseUserTimeToHM(time);
        return parsed || time;
    }
}
